
<template>
  <BaseModal :model-value="true" ariaLabel="Existing patient visit"  @close="onCancel" >
    <template #header>
      <div class="modal-title">
        <template v-if="step === 'search'">
          <h4>
            <i class="fa-solid fa-magnifying-glass"></i> Existing Patient search
          </h4>
          <p>Search for patient and configure encounter details</p>
        </template>

        <!-- FORM STEP HEADER -->
        <template v-else>
          <h4><i class="fa-solid fa-user"></i> New Encounter</h4>
          <p>Configure encounter details</p>
        </template>

      </div>
    </template>

    <div class="modal-body">
      <!-- STEP 1 — SEARCH -->
      <div v-if="step === 'search'">

        <!-- Search input -->
        <div class="input-group w-100">
          <input
            v-model="phoneNumber"
            id="phoneNumber"
            type="text"
            placeholder="Enter Phone Number"
            class="form-control"
            @keyup.enter="searchPhone"
          />
          <button
            class="btn btn-primary"
            @click="searchPhone"
            :disabled="isSearching || !phoneNumber"
          >
            <span v-if="isSearching">Searching…</span>
            <span v-else>Search</span>
          </button>
        </div>

        <!-- SEARCH RESULTS -->
        <div v-if="searchDone" class="mt-3 w-100">

          <!-- No results -->
          <div v-if="patients.length === 0" class="alert alert-danger py-2 px-3">
            <i class="fa-solid fa-circle-exclamation"></i>
            No patients found for {{ phoneNumber }}.  
            Please fill in the information below.
          </div>

          <!-- Results available -->
          <div v-else class="card p-3 bg-light w-100">
            <h5 class="mb-2">Select a patient</h5>

            <div class="list-group">
              <label
                v-for="p in patients"
                :key="p.id"
                class="list-group-item list-group-item-action d-flex align-items-start gap-3"
                @click="pickPatient(p)"
              >
                <input
                  class="form-check-input mt-1"
                  type="radio"
                  name="patientPick"
                  :value="p.id"
                  v-model="selectedPatientId"
                />

                <div>
                  <div class="fw-semibold">
                    {{ p.name }}
                    <span class="text-muted small">(#{{ p.id }})</span>
                  </div>

                  <div class="small text-muted">
                    Phone: {{ p.phone_number }}  
                    • DOB: {{ p.date_of_birth }}
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- STEP 2 — FORM -->
      <div v-else>
        <div class=" selected-patient-summarycard px-4">
          <div class="d-flex justify-content-between">
            <h6>Selected patient</h6>
            <h6 @click="backToSearch" style="cursor: pointer;">change</h6>
          </div>
        <div class="card patient-card">
          <div>
              <div 
              class="user-circle me-4" 
              aria-expanded="false">
              {{ selectedPatient.name.charAt(0).toUpperCase() }}
          </div>
          </div>
          <div>
            <h6 class="mb-0">{{ selectedPatient.name }}</h6>
            <small> DOB:{{ selectedPatient.date_of_birth }}</small>
          </div>
        </div>
        <div>
          <h6> <i class="fa-solid fa-file-lines me-1"></i> Encounter Details</h6>
        </div>
        <div class="row">
           <div class="input-group w-50">
            <label for="encounterMode" class="input-label">Encounter Mode</label>
            <select v-model="encounterMode" id="encounterMode" class="form-select w-100">
              <option value="online">Online</option>
              <option value="offline">Offline</option>
            </select>
          </div>
          <div class="input-group w-50">
            <label for="DOC" class="input-label">Date of Complaint</label>
            <input v-model="DOC" id="DOC" type="date" required placeholder="Enter Date of Complaint" class="form-control w-100" />
          </div>
        </div>
        <div class="row">
          <div class="input-group w-50">
            <label for="DOS" class="input-label">Date of Service</label>
            <input v-model="DOS" id="DOS"   type="date" required placeholder="Enter Date of Service" class="form-control w-100" />
          </div>
          <div class="input-group w-50">
            <label for="doctorName" class="input-label">Doctor Name</label>
            <input v-model="doctorName" id="doctorName" type="text" required placeholder="Enter Doctor name" class="form-control w-100" />
          </div>
        </div>
        <div class="route-options">
          <label for="route-radio" class="mb-2 mt-2"><i class="fas fa-language me-1 "></i> Language configurations</label> 
          <div class="route-label card p-2 d-flex justify-content-center">
            <input type="radio" v-model="selectedRoute"  value="record" class="route-radio form-check-input " checked/>
            <span class="ms-3">Single language</span>
          </div>
          <div class="route-label card p-2 d-flex justify-content-center">
            <input type="radio" v-model="selectedRoute" value="translate" class="route-radio form-check-input" />
            <span class="ms-3">Multi language</span> 
          </div>
        </div>
        <div v-if="selectedRoute === 'translate'" class="language-selection row mb-2" >
          <div class="input-group w-50 d-flex flex-column">
            <label for="doctor-language">Doctor Language</label>
            <select v-model="doctorLanguage" id="doctor-language" class="form-select w-100">
              <option value="en">English</option>
              <option value="es">Spanish</option>
            </select>
          </div>
          <div class="input-group w-50 d-flex flex-column">
            <label for="patient-language">Patient Language</label>
            <select v-model="patientLanguage" id="patient-language" class="form-select w-100">
              <option value="en">English</option>
              <option value="es">Spanish</option>
            </select>
          </div>
        </div>
      </div>
      </div>

    </div>

    <template #footer>
      <div class="modal-footer mt-4">

        <button class="btn btn-outline-dark me-4" @click="onCancel">
          Cancel
        </button>
        <button v-if="step === 'form'"  class="btn btn-primary" :disabled="!canSubmit || isSubmitting" @click="handleSubmit" >
          {{ isSubmitting ? 'Starting...' : 'Start' }}
        </button>
      </div>
    </template>

  </BaseModal>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue'
import BaseModal from '@/components/ui/BaseModal.vue'
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import { useWebSocket } from '../../utils/useWebSocket';
import { addPatientService } from "@/services/api/patientService";
import { getNewTopicIdService } from "@/services/api/encounterService"
// eslint-disable-next-line
const props = defineProps({
  fetchPatients: { type: Function, required: true }
})
// eslint-disable-next-line
const emit = defineEmits(['close'])

const step = ref('search')
const phoneNumber = ref('')
const patients = ref([])
const isSearching = ref(false)
const selectedPatient = ref(null)
const selectedPatientId = ref(null)
const searchDone = ref(false)
const doctorName = localStorage.getItem('username') || ref('');
const store = useStore();
const selectedRoute = ref('record');
const doctorLanguage = ref('en'); 
const patientLanguage = ref('es'); 
const token = localStorage.getItem('token');
const webSocket = ref(null);
const router = useRouter();
const route = useRoute();
const toast = useToast();
const DOS = new Date().toISOString().slice(0, 10);
const DOC = ref('');

const encounterMode = ref('online');
const ehrEncounterId =  route.query.ehrEncounterId;
const ehrPatientName = route.query.ehrPatientName || ref('');
const ehrPatientDOB =  route.query.ehrPatientDOB|| ref('');
const ehrOrgName =  route.query.ehrOrgName|| ref('');
const ehrPatientId =  route.query.ehrPatientId|| ref('');

const form = ref({
  visitDate: new Date().toISOString().slice(0, 10),
  complaint: '',
  notes: ''
})
const isSubmitting = ref(false)

const handleSubmit = async () => {
  if (!selectedPatientId.value) {
    const newPatient = await addPatient();
    if (!newPatient) return; 
  }

  const topicId = await getNewTopicId();
  if (!topicId) {
    toast.error('Unable to start encounter');
    return;
  }

  if (topicId) {
    const dialogId ="2";
    const doctorLang= doctorLanguage.value
    const patientLang= patientLanguage.value
    const patientNameV = ehrPatientName
    localStorage.setItem('encounterMode', encounterMode.value);
    const wsUrl  = `${process.env.VUE_APP_SOFA_WS}${topicId}/${dialogId}/${selectedPatientId.value}`;
    if (selectedRoute.value === 'record') {
        store.commit('setPatientName', patientNameV);
        store.commit('setPatientId', selectedPatientId.value);
        store.commit('setTopicId', topicId);
        store.commit('setDOB',ehrPatientDOB)
        store.commit('setDOC',DOC)
        store.commit('setDOS',DOS)
        if(ehrPatientId.value === ""){
          const data = { topicId, patientNameV, ehrPatientName, view: 'record' };
          store.dispatch('navigation/saveNavigationData', data);
          await router.push({ name: 'encounter' });
        }else{
          const data = {   patientNameV ,ehrEncounterId, ehrPatientName,ehrPatientId, ehrOrgName,view: 'record'};
          store.dispatch('navigation/saveNavigationData', data);
        await router.push({ name: 'encounter'});
        }
    } else if (selectedRoute.value === 'translate') {
      store.commit('setPatientName', patientNameV);
      store.commit('setTopicId', topicId);
      store.commit('setPatientId', selectedPatientId.value);
      store.commit('setDOB',ehrPatientDOB)
      store.commit('setDOC',DOC)
      store.commit('setDOS',DOS)
      const data = { topicId, patientNameV, doctorLang,patientLang, view: 'translate' };
      // console.log(doctorLang,patientLang)
      store.dispatch('navigation/saveNavigationData', data);
      // console.log(data)
      await router.push({ name: 'encounter' });
    } else {
      console.warn('No matching route');
      toast.warning('No matching route');
      return;
  }
  await nextTick();
    const handleMessage = (data) => {
      console.log('Message received from WebSocket:', data);
    };
    webSocket.value = useWebSocket(wsUrl, token,handleMessage);
  }
  onSubmit()
};
const addPatient = async () => {
  const patientData = {
    name: ehrPatientName.value,
    date_of_birth: ehrPatientDOB.value,
    phone_number: phoneNumber.value,
  };
  const created = await addPatientService(patientData);

  if (!created?.patient_id) {
    console.error('No patient ID returned:', created.data);
    toast.error('Failed to add patient');
    return null;
  }

  selectedPatientId.value = created.patient_id;
  patients.value.unshift(created);
  toast.success("Patient added successfully");
  return created;
};
const getNewTopicId = async () => {
  const response = await getNewTopicIdService(ehrEncounterId);
  return response.id;
};


const searchPhone = async () => {
  if (!phoneNumber.value) return

  isSearching.value = true
  searchDone.value = false
  selectedPatientId.value = null
  selectedPatient.value = null
  patients.value = []

  try {
    const result = await props.fetchPatients(phoneNumber.value)
    console.log('fetchPatients result:', result)

    patients.value = Array.isArray(result) ? result : []
  } catch (err) {
    console.error('fetchPatients error:', err)
    patients.value = []
  }
  searchDone.value = true
  isSearching.value = false
}

const pickPatient = (p) => {
  selectedPatient.value = p
  selectedPatientId.value = p.id
  step.value = 'form'
}

const backToSearch = () => {
  step.value = 'search'
}

const canSubmit = computed(() =>
  selectedPatient.value && form.value.visitDate
)

const onCancel = () => emit('close', null)

const onSubmit = () => {
  if (!canSubmit.value) return

  isSubmitting.value = true

  emit('close', {
    patient: selectedPatient.value,
    visitData: { ...form.value }
  })

  isSubmitting.value = false
}
</script>

<style scoped>
.modal-body {
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 10px;
}
.route-options {
  margin-bottom: 20px;
}

.route-label {
  display: block;
  margin-bottom: 10px;
  font-size: 14px;
  cursor: pointer;
  position: relative;
  padding-left: 15px;
}

.route-radio {
  position: absolute;
}

.modal-actions {
  display: flex;
  justify-content: space-between;
}

.submit-btn {
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
  width: 10rem;
  background-color: #518DB1;
  color: white;
  margin-right: 10px;
}
.input-field{
  border-radius: 8px !important;
}
.addBtn{
  background-color: #FFF
}
.addBtn:hover{
  background-color:#518DB1;
}
.card{
  background-color: #f5f9fe;
}
.user-circle {
  width: 40px;
  height: 40px;
  background-color: #d0e1fc;
  color: #3c83f6;
  font-size: 18px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  text-transform: uppercase;
  transition: 0.3s;
}
.patient-card{
  padding: 1rem;
  border: 2px solid #3c83f6;
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 2rem;
}
i{
  color: #3ee0cf
}

</style>
